/**
 * EmpowerSkillEffect — Conjuration Bind path evolution: tactical companion.
 *
 * Replaces Bind. Spawns a single companion familiar with upgraded stats
 * (95 HP, 14 damage) and tactical AI mode. Three new AI behaviors:
 *   - Stunned-target priority: engages staggered enemies over healthy ones
 *   - Retreat behavior: disengages when HP < 50%, follows player until healed
 *   - Threat positioning: positions between player and most dangerous enemy
 *
 * Adapted behavior is permanent at this tier — the familiar always resolves
 * its secondary on-hit effect from the player's highest non-Conjuration tree,
 * without requiring the Resonant resolver gate.
 *
 * Upgrade hooks (inherited from Bind path, resolved via 'bind' scope):
 *   - Fortify (bind_fortify): hpBonus additive (+25), interceptEnabled boolean
 *   - Resonant (bind_resonance): damageBonus additive (+5)
 *
 * The companion's tactical AI behavior (target selection, retreat logic,
 * threat-based positioning) is handled by the integration layer. This skill
 * is responsible only for the spawn/despawn lifecycle, config assembly, and
 * resolver key resolution.
 *
 * Medium cooldown tier (1200ms). Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see conjuration-spec.md § Empower
 */

import type { SkillEffect } from '../../SkillEffect.js';
import {
  type CompanionSummonConfig,
  type AdaptedBehaviorConfig,
  resolveAdaptedBehavior,
} from './BindSkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface EmpowerDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    hasEffect(skillId: string, key: string): boolean;
    getMinEffect(skillId: string, key: string): number | undefined;
    getMaxEffect(skillId: string, key: string): number | undefined;
  };
  readonly player: {
    getPosition(): { x: number; y: number };
  };
  readonly targeting: {
    /** Returns true if the companion should fail to manifest (XP-based freshness check). */
    shouldFizzle(): boolean;
  };
  readonly summons: {
    spawn(config: CompanionSummonConfig, x: number, y: number): string;
  };
  readonly progression: {
    /** Returns the tree ID with the most XP among all non-Conjuration trees, or null if none. */
    getHighestNonConjurationTree(): string | null;
  };
}

// ---------------------------------------------------------------------------
// Skill tuning
// ---------------------------------------------------------------------------

export interface EmpowerSkillConfig {
  readonly cooldownMs: number;
  /** Base companion HP before upgrade bonuses. */
  readonly baseHp: number;
  /** Base companion damage per hit before upgrade bonuses. */
  readonly baseDamage: number;
  /** Base companion movement speed. */
  readonly baseSpeed: number;
  /** Max distance from player before returning (tiles). */
  readonly leashRadiusTiles: number;
  /** Max distance from familiar to target before engaging (tiles). */
  readonly attackRangeTiles: number;
  /** Delay between swings in ms. */
  readonly attackRecoveryMs: number;
  /** How much enemy aggro the familiar draws (0–1). */
  readonly baseAggroWeight: number;
  /** HP fraction (0–1) below which the familiar retreats to follow mode. */
  readonly retreatHpThreshold: number;
}

const DEFAULT_CONFIG: EmpowerSkillConfig = {
  cooldownMs: 1200,              // medium cooldown tier
  baseHp: 95,                    // spec: 95 HP (up from Bind's 45)
  baseDamage: 14,                // spec: 14 damage per hit (up from Bind's 6)
  baseSpeed: 1.0,                // integration layer applies px/s
  leashRadiusTiles: 2,           // spec: stay within 2 tiles of player
  attackRangeTiles: 3,           // spec: attack enemies within 3 tiles
  attackRecoveryMs: 1000,        // spec: 1s recovery between swings
  baseAggroWeight: 0.3,          // moderate aggro draw
  retreatHpThreshold: 0.5,       // spec: retreat when HP < 50%
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class EmpowerSkillEffect implements SkillEffect {
  readonly skillId = 'empower';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly failLabel = 'FIZZLE';

  private readonly deps: EmpowerDeps;
  private readonly config: EmpowerSkillConfig;

  private _cooldownRemaining = 0;
  private _familiarAlive = false;
  private _familiarId = '';
  private _fizzled = false;
  private _spawnsCount = 0;
  private _resolvedHp = 0;
  private _resolvedDamage = 0;
  private _interceptEnabled = false;
  private _interceptCooldownMs = 0;
  private _interceptMinHp = 0;
  private _adaptedBehavior: AdaptedBehaviorConfig = { type: 'none' };

  constructor(deps: EmpowerDeps, config: Partial<EmpowerSkillConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters (observable by scenarios and integration layer) ---

  get cooldown(): number { return this._cooldownRemaining; }
  get familiarAlive(): boolean { return this._familiarAlive; }
  get familiarId(): string { return this._familiarId; }
  get fizzled(): boolean { return this._fizzled; }
  get spawnsCount(): number { return this._spawnsCount; }
  get resolvedHp(): number { return this._resolvedHp; }
  get resolvedDamage(): number { return this._resolvedDamage; }
  get interceptEnabled(): boolean { return this._interceptEnabled; }
  get interceptCooldownMs(): number { return this._interceptCooldownMs; }
  get interceptMinHp(): number { return this._interceptMinHp; }
  get adaptedBehavior(): AdaptedBehaviorConfig { return this._adaptedBehavior; }

  // --- Integration layer hook ---

  /**
   * Called by the integration layer when the companion dies.
   * Wired to SummonSystem's 'died' event for archetype 'companion'.
   */
  onFamiliarDied(): void {
    this._familiarAlive = false;
    this._familiarId = '';
  }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0 && !this._familiarAlive;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Freshness fizzle check — consumes cooldown but does not spawn
    this._fizzled = this.deps.targeting.shouldFizzle();
    if (this._fizzled) {
      this._cooldownRemaining = this.config.cooldownMs;
      return false;
    }

    const pos = this.deps.player.getPosition();

    // Resolve HP: base + additive bonus from Fortify (uses 'bind' scope)
    const hpBonus = this.deps.resolver.getAdditiveEffect('bind', 'hpBonus');
    this._resolvedHp = this.config.baseHp + hpBonus;

    // Resolve damage: base + additive bonus from Resonant (uses 'bind' scope)
    const damageBonus = this.deps.resolver.getAdditiveEffect('bind', 'damageBonus');
    this._resolvedDamage = this.config.baseDamage + damageBonus;

    // Resolve intercept behavior (Fortify upgrade)
    const interceptEnabled = this.deps.resolver.hasEffect('bind', 'interceptEnabled');
    this._interceptEnabled = interceptEnabled;

    this._interceptCooldownMs = interceptEnabled
      ? (this.deps.resolver.getMinEffect('bind', 'interceptCooldownMs') ?? 2000)
      : 0;

    this._interceptMinHp = interceptEnabled
      ? (this.deps.resolver.getMaxEffect('bind', 'interceptMinHp') ?? 15)
      : 0;

    // Adapted behavior — permanent at Empower tier, always resolved from
    // the player's highest non-Conjuration tree. No resolver gate.
    this._adaptedBehavior = resolveAdaptedBehavior(
      this.deps.progression.getHighestNonConjurationTree(),
    );

    const companionConfig: CompanionSummonConfig = {
      id: 'empowered_companion',
      archetype: 'companion',
      baseHp: this._resolvedHp,
      baseDamage: this._resolvedDamage,
      baseSpeed: this.config.baseSpeed,
      leashRadiusTiles: this.config.leashRadiusTiles,
      aggroWeight: this.config.baseAggroWeight,
      aiMode: 'tactical',
      attackRangeTiles: this.config.attackRangeTiles,
      attackRecoveryMs: this.config.attackRecoveryMs,
      stunTargetPriority: true,
      retreatHpThreshold: this.config.retreatHpThreshold,
      threatPositioning: true,
      ...(interceptEnabled && {
        interceptEnabled: true,
        interceptCooldownMs: this._interceptCooldownMs,
        interceptMinHp: this._interceptMinHp,
      }),
      ...(this._adaptedBehavior.type !== 'none' && {
        adaptedBehavior: this._adaptedBehavior,
      }),
    };

    this._familiarId = this.deps.summons.spawn(companionConfig, pos.x, pos.y);
    this._familiarAlive = true;
    this._spawnsCount++;

    this._cooldownRemaining = this.config.cooldownMs;
    return true;
  }

  deactivate(): void {
    // Instant skill — no toggle state
  }

  isActive(): boolean {
    return false;
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
    this._familiarAlive = false;
    this._familiarId = '';
  }
}
